import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Title } from "@angular/platform-browser";

import { select, Store } from "@ngrx/store";
import { Observable, Subscription, zip } from "rxjs";
import { map, skip } from "rxjs/operators";

import {
  getCategoriesForListByUsage,
  getCustomerExcelList,
  getCustomerPartyList,
  getCustomerTableConfig,
  getPropertiesForListByUsage,
  resetPartyState,
  saveCustomerTableConfig,
} from "src/app/store/actions/parties/parties.actions";
import {
  AppState,
  loadedCustomerPartyListState,
  getLoadingPartyState,
  loadedCustomerPartyListExcelState,
  loadedCustomerPartyListTotalState,
  loadedCategoriesListPartyState,
  loadedPropertiesListPartyState,
  loadedMenusState,
  loadedCustomersTableConfig,
} from "src/app/store/reducers";

import {
  ColumnSorterInfo,
  ConfigScope,
  ConfigSettingType,
  DefaultPage,
  ExportEvent,
  FieldType,
  FilterEvent,
  FormatType,
  PageEvent,
  SearchEvent,
  SortEvent,
  TableConfig,
  TableEvent,
} from "src/app/models/custom-table/custom-table.model";
import { CategoryDTO } from "src/app/models/Categories/categoryDTO";
import { PropertyDTO } from "src/app/models/Properties/propertyDTO";
import { MenuDTO, RouteData } from "src/app/models/Menu/MenuDTO";
import { CustomerPartyDTO } from "src/app/models/Party/customerPartyDTO";

import { setCategories, setColumnInfoCategoriesProperties, setProperties } from "src/app/shared/utils/tableUtils";
import { subMenusIterator } from "src/app/shared/utils/menuUtils";
import { CHIP_STYLE, getChipStyle } from 'src/app/shared/utils/customTableUtils';

const USAGE_CODE = "CUSTOMER";

@Component({
  selector: "app-customer-list",
  templateUrl: "./customer-list.component.html",
  styleUrls: ["./customer-list.component.scss"],
})
export class CustomerListComponent implements OnInit, OnDestroy {
  public isLoading$: Observable<boolean>;

  columnInfo$: Observable<ColumnSorterInfo[]>;

  public customerList$: Observable<CustomerPartyDTO[]>;
  public excelCustomerList$: Observable<CustomerPartyDTO[]>;
  public totalCustomers$: Observable<number>;

  multiSubs: Subscription;
  loadedCustomersSub: Subscription;
  customerList: CustomerPartyDTO[];

  categories$: Observable<CategoryDTO[]>;
  properties$: Observable<PropertyDTO[]>;

  menus$: Observable<MenuDTO[]>;
  menuSub: Subscription;

  formPath: string = "customers/customer";
  tableTitle: string;
  headerIcon: string;

  tableConfig: TableConfig[] = [
    {
      header: "CustomGrid.Options",
      field: FieldType.OPTIONS,
      fieldType: FieldType.OPTIONS,
    },
    {
      header: "Customers.code",
      field: "code",
      fieldType: FieldType.STRING,
      searchable: true,
      hidden: true,
      identifier: true,
    },
    {
      header: "Customers.customerCode",
      field: "customerCode",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Customers.Status",
      field: "statusDescription",
      fieldType: FieldType.CHIP_STRING,
    },
    {
      header: "Customers.vatIN",
      field: "vatin",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Customers.fullName",
      field: "name",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Customers.salutation",
      field: "salutationDescription",
      fieldType: FieldType.STRING
    },
    {
      header: "Customers.birthday",
      field: "birthday",
      fieldType: FieldType.DATE,
      formatType: FormatType.DATE,
      hidden: true
    },
    {
      header: "Customers.Phone",
      field: "contacts[PHONE].contact",
      fieldType: FieldType.STRING,
      hidden: true
    },
    {
      header: "Customers.Email",
      field: "contacts[EMAIL].contact",
      fieldType: FieldType.STRING,
      hidden: true
    },
    {
      header: "Customers.Mobile",
      field: "contacts[MOBILE].contact",
      fieldType: FieldType.STRING,
      hidden: true
    },
    {
      header: "Customers.StreetName",
      field: "address.street",
      fieldType: FieldType.STRING,
      hidden: true
    },
    {
      header: "Customers.ZipCode",
      field: "address.postalZipCode",
      fieldType: FieldType.STRING,
      hidden: true
    },
    {
      header: "Customers.Country",
      field: "address.isoCountryCode",
      fieldType: FieldType.COUNTRY,
      hidden: true
    },
    {
      header: "Customers.AddressDesc",
      field: "address.description",
      fieldType: FieldType.STRING,
      hidden: true
    },
    {
      header: "Customers.CreatedOn",
      field: "createdOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME
    },
    {
      header: "Customers.Updated",
      field: "updatedOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME
    },
  ];

  constructor(
    private store: Store<AppState>,
    private titleService: Title,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.isLoading$ = this.store.pipe(select(getLoadingPartyState));
    this.customerList$ = this.store.pipe(select(loadedCustomerPartyListState));
    this.excelCustomerList$ = this.store.pipe(select(loadedCustomerPartyListExcelState));
    this.totalCustomers$ = this.store.pipe(select(loadedCustomerPartyListTotalState));
    this.categories$ = this.store.pipe(select(loadedCategoriesListPartyState));
    this.properties$ = this.store.pipe(select(loadedPropertiesListPartyState));
    this.menus$ = this.store.pipe(select(loadedMenusState));
    this.columnInfo$ = this.store.pipe(select(loadedCustomersTableConfig)).pipe(
      map((columnInfo) => {
        if (columnInfo) {
          return setColumnInfoCategoriesProperties(columnInfo, this.tableConfig);
        }
      })
    );

    this.loadScreenTitle();
    this.getCustomerCategoriesProperties();
  }

  loadScreenTitle() {
    this.menuSub = this.menus$.subscribe((menus: MenuDTO[]) => {
      if (menus && menus.length) {
        this.route.data.subscribe((routeData: RouteData) => {
          menus.forEach((menuEl: MenuDTO) => {
            const selectedMenu = subMenusIterator(menuEl, routeData);
            if (selectedMenu) {
              this.headerIcon = selectedMenu.icon;
              this.tableTitle = selectedMenu.description;
              this.titleService.setTitle(this.tableTitle);
            }
          })
        });
      }
    });
  }

  setTableCustomElements() {
    this.loadedCustomersSub = this.customerList$
      .pipe(skip(1))
      .subscribe((customers: CustomerPartyDTO[]) => {
        this.customerList = customers;
        if (this.customerList !== undefined && this.customerList !== null && this.customerList?.length > 0) {
          this.customerList = this.customerList.map((customer) => {
            let treatedCustomer = customer;
            treatedCustomer = this.setCustomFields(treatedCustomer);
            return treatedCustomer;
          });
        }
      });
  }

  setCustomFields(customer: any) {
    const defaultCustomer = {
      ...customer,
    };
    const { statusDescription, statusCode } = customer;
    if (statusDescription === undefined) {
      return defaultCustomer;
    }

    let style: CHIP_STYLE = CHIP_STYLE.INACTIVE;
    if (statusDescription !== undefined && statusDescription !== null) {
      if (statusCode !== undefined && statusCode !== null){
        switch (statusCode) {
          case 'ACTIVE':
            style = CHIP_STYLE.SUCCESS;
            break;
          case 'DISABLED':
            style = CHIP_STYLE.INACTIVE;
            break;
          case 'BLOCKED':
            style = CHIP_STYLE.DANGER;
            break;
        }
      }

      let styleSettings = getChipStyle(style);

      let statusChip =  {
        ...customer,
        statusDescription: {
            value: statusDescription,
            icon: styleSettings.icon,
            style: styleSettings.style,
        },
        address: {
          ...customer.address,
          isoCountryCode: {
            code: customer.address.isoCountryCode,
            alpha2Code: customer.address.countryAlpha2Code,
          }
        }
      };
     return statusChip;
    }
  }

  getCustomerCategoriesProperties() {
    this.multiSubs = zip(
      this.categories$.pipe(skip(1)),
      this.properties$.pipe(skip(1))
    ).subscribe(([categories, properties]) => {
      if (categories && categories?.length) {
        const tableConfigCategories = setCategories(categories);
        if (tableConfigCategories) {
          this.tableConfig = this.tableConfig.concat(tableConfigCategories);
        }
      }

      if (properties && properties?.length) {
        const tableConfigProperties = setProperties(properties);
        if (tableConfigProperties) {
          this.tableConfig = this.tableConfig.concat(tableConfigProperties);
        }
      }

      this.getTableConfig();
      this.getCustomersList();
      this.setTableCustomElements();
    });

    this.store.dispatch(
      getCategoriesForListByUsage({
        pageReq: null,
        usageCode: USAGE_CODE,
      })
    );

    this.store.dispatch(
      getPropertiesForListByUsage({
        pageReq: null,
        usageCode: USAGE_CODE,
      })
    );
  }

  getCustomersList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    this.store.dispatch(
      getCustomerPartyList({
        options: {
          paging: {
            skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
            top: pageSize ?? DefaultPage.PAGE_SIZE,
            realSize: true,
          },
          filteringArgs: searchEvent,
          sortingArgs: sortEvent,
          advancedFilteringArgs: filterEvent,
        },
      })
    );
  }

  getCustomersExcelList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    this.store.dispatch(
     getCustomerExcelList({
        options: {
          paging: {
            skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
            top: pageSize ?? DefaultPage.EXCEL_PAGE_SIZE,
          },
          filteringArgs: searchEvent,
          sortingArgs: sortEvent,
          advancedFilteringArgs: filterEvent,
        },
      })
    );
  }

  getTableConfig() {
    this.store.dispatch(
      getCustomerTableConfig({
        options: { code: USAGE_CODE, settingType: ConfigSettingType.TABLE_CONFIG },
      })
    );
  }

  onGetTableData(tableEvent: TableEvent) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = tableEvent;
    this.getCustomersList(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  onGetExportData(exportEvent: ExportEvent) {
    const { pageEvent, sortEvent, searchEvent, filterEvent } = exportEvent;
    this.getCustomersExcelList(pageEvent, sortEvent, searchEvent, filterEvent);
  }

  onSaveTableConfig(tableColumnInfo: ColumnSorterInfo[]) {
    this.store.dispatch(
      saveCustomerTableConfig({
        config: {
          code: USAGE_CODE,
          settingTypeCode: ConfigSettingType.TABLE_CONFIG,
          scope: ConfigScope.USER,
          value: { tableColumnInfo },
        },
      })
    );
  }

  ngOnDestroy() {
    if (this.loadedCustomersSub){
      this.loadedCustomersSub.unsubscribe();
    }
    if (this.multiSubs) {
      this.multiSubs.unsubscribe();
    }
    if (this.menuSub) {
      this.menuSub.unsubscribe();
    }
    this.store.dispatch(resetPartyState());
  }
}
